/**
 * Rich Menu 工具 Modal(REQ-20260519001):
 * 用 OpenAI gpt-image-1 生 LINE 主選單圖片,直接 b64 預覽,下載後手動到 LINE 後台上傳。
 * 不存 DB / S3 — 一次性產物。
 */
import { useState } from 'react';
import {
  LINE_RICHMENU_DEFAULT_PROMPT,
  LINE_RICHMENU_QUALITIES,
  LINE_RICHMENU_SIZES,
  type LineRichMenuQuality,
  type LineRichMenuSize,
} from '@app/shared';
import { Modal } from '../../components/Modal';
import { useGenerateRichMenu } from './lineApi';

interface Props {
  open: boolean;
  onClose: () => void;
}

const SIZE_LABEL: Record<LineRichMenuSize, string> = {
  '1536x1024': '橫向 1536×1024(接近 LINE 主選單 full,3:2)',
  '1024x1024': '方形 1024×1024',
  '1024x1536': '直向 1024×1536',
};

const QUALITY_LABEL: Record<LineRichMenuQuality, string> = {
  low: '低(快、便宜)',
  medium: '中(預設)',
  high: '高(慢、貴)',
};

export function RichMenuToolModal({ open, onClose }: Props): JSX.Element {
  const [prompt, setPrompt] = useState(LINE_RICHMENU_DEFAULT_PROMPT);
  const [size, setSize] = useState<LineRichMenuSize>('1536x1024');
  const [quality, setQuality] = useState<LineRichMenuQuality>('medium');
  const gen = useGenerateRichMenu();

  const onGenerate = (): void => {
    if (gen.isPending) return;
    gen.mutate({ prompt, size, quality });
  };

  const dataUrl = gen.data ? `data:${gen.data.mimeType};base64,${gen.data.b64}` : null;
  const filename = `line-richmenu-${size}-${Date.now()}.png`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="LINE Rich Menu 生圖工具"
      widthClass="max-w-3xl"
      dismissOnBackdrop={!gen.isPending}
    >
      <div className="grid gap-4">
        <p className="text-xs text-slate-500 leading-relaxed">
          用系統設定的 OpenAI 生圖提供商產生 LINE 主選單(Rich Menu)圖片(實際模型以「設定 → OpenAI 生圖」內為準,生成後也會顯示在結果列)。
          下載 PNG 後到 LINE Developers / OA Manager 上傳 → 設定按鈕區域動作為「傳送訊息:我要詢價」即可。
          LINE Rich Menu 完整版尺寸 2500×1686,選擇 <code>1536×1024</code> 後到 LINE 後台會由系統等比縮放,
          UI 設計時讓「詢價」按鈕在畫面中央且四週留白會比較不會被截到。
        </p>

        <label className="grid gap-1 text-sm">
          <span className="text-slate-700">提示詞(英文效果較好)</span>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={9}
            className="border rounded px-2 py-1.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
            disabled={gen.isPending}
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="grid gap-1 text-sm">
            <span className="text-slate-700">尺寸</span>
            <select
              value={size}
              onChange={(e) => setSize(e.target.value as LineRichMenuSize)}
              disabled={gen.isPending}
              className="border rounded px-2 py-1.5 text-sm"
            >
              {LINE_RICHMENU_SIZES.map((s) => (
                <option key={s} value={s}>
                  {SIZE_LABEL[s]}
                </option>
              ))}
            </select>
          </label>

          <label className="grid gap-1 text-sm">
            <span className="text-slate-700">畫質</span>
            <select
              value={quality}
              onChange={(e) => setQuality(e.target.value as LineRichMenuQuality)}
              disabled={gen.isPending}
              className="border rounded px-2 py-1.5 text-sm"
            >
              {LINE_RICHMENU_QUALITIES.map((q) => (
                <option key={q} value={q}>
                  {QUALITY_LABEL[q]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex gap-2 items-center">
          <button
            type="button"
            onClick={onGenerate}
            disabled={gen.isPending || prompt.trim().length < 5}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {gen.isPending ? '生圖中(可能需要 1~3 分鐘)…' : '生成 Rich Menu 圖'}
          </button>
          {gen.data ? (
            <a
              href={dataUrl ?? '#'}
              download={filename}
              className="border border-emerald-600 text-emerald-700 px-4 py-2 rounded hover:bg-emerald-50"
            >
              ⬇ 下載 PNG
            </a>
          ) : null}
        </div>

        {gen.isError ? (
          <div role="alert" className="text-red-600 text-sm bg-red-50 border border-red-200 rounded p-2">
            生圖失敗:{gen.error instanceof Error ? gen.error.message : '未知錯誤'}
          </div>
        ) : null}

        {gen.data && dataUrl ? (
          <div className="grid gap-2">
            <div className="text-xs text-slate-500">
              模型:{gen.data.model} · 尺寸:{gen.data.size} · 耗時:{(gen.data.latencyMs / 1000).toFixed(1)}s
            </div>
            <div className="border rounded bg-slate-100 p-2 flex items-center justify-center">
              <img
                src={dataUrl}
                alt="Rich Menu 預覽"
                className="max-w-full max-h-[60vh] object-contain"
              />
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
